import { useEffect, useState } from "react";
import { FiCheck } from "react-icons/fi";

import type { BriefingReportPageData } from "content-ui/briefings/fetchers/briefingReportFetcher";

import Heading from "content-ui/briefings/components/briefingsReports/elements/heading";
import CardContainer from "content-ui/briefings/components/briefingsReports/elements/cardContainer";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";

import styles from "./feedback.module.css";

const FeedbackCard = ({
  briefing,
  id,
}: {
  briefing: BriefingReportPageData;
  id: string;
}) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const [clicked, setClicked] = useState<number | null>(null);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const { track } = useMixpanelTracking();

  useEffect(() => {
    if (!clicked) return;

    const timer = setTimeout(() => setShowMessage(true), 1250);

    const asyncFn = async () => {
      await track("Briefing Feedback", {
        Rating: clicked,
        "Briefing title": briefing.name,
      });
    };

    asyncFn();

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clicked]);

  return (
    <CardContainer id={`content__${id}`}>
      <Heading text={"Your feedback"} id={id} />
      <div className="flex flex-col justify-start md:flex-row md:items-start">
        <p className="pb-[10px] text-[20px] font-[700] text-[#002EA5] md:flex-1 md:text-[24px]">
          How would you rate this briefing?
        </p>
        <div className="relative flex w-100 max-w-[340px] flex-1 justify-between overflow-hidden md:max-w-none md:justify-start">
          {showMessage ? (
            <div className="flex items-center pt-2 md:pt-0">
              <FiCheck className="mr-2 inline-block text-[#002EA5]" />
              <p className="text-[20px] md:text-[24px]">
                Thanks for your rating!
              </p>
            </div>
          ) : (
            Array.from({ length: 5 }, (_, i) => i + 1).map((i) => {
              return (
                <div
                  key={i}
                  className="-ml-2 flex flex-col items-center md:ml-0"
                >
                  <div
                    className={`${
                      clicked ? "pointer-events-none" : ""
                    } contents`}
                    role="radio"
                    aria-label={`Rate the briefing ${i} out of 5`}
                    aria-checked={i === clicked}
                    tabIndex={0}
                    onMouseOver={() => setHovered(i)}
                    onFocus={() => setHovered(i)}
                    onClick={() => setClicked(i)}
                    onKeyDown={() => setClicked(i)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <label
                      htmlFor={`rating-${i}`}
                      className={
                        `${styles["rating-label"]} h-12 w-16 ` +
                        ((clicked && i <= clicked) || (hovered && i <= hovered)
                          ? styles["filled-star"]
                          : "")
                      }
                    >
                      <input
                        className="hidden"
                        type="radio"
                        name="rating"
                        id={`rating-${i}`}
                        value={i}
                        disabled={Boolean(clicked)}
                      />
                    </label>
                  </div>
                  <div className="select-none text-cerise">{i}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </CardContainer>
  );
};

export default FeedbackCard;
